import { useState, type FormEvent } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Dialog } from '~/components/ui/Dialog';
import { apiCreateProject } from '~/lib/api/projects';
import type { Project } from '@shared/types/project';

interface NewProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (project: Project) => void;
}

type TemplateId = 'landing';

export default function NewProjectDialog({ open, onOpenChange, onCreated }: NewProjectDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const template: TemplateId = 'landing';
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!name.trim()) {
      return;
    }

    setLoading(true);

    try {
      const project = await apiCreateProject({
        name: name.trim(),
        description: description.trim() || undefined,
        template,
      });
      setName('');
      setDescription('');
      onOpenChange(false);
      onCreated(project);
    } catch {
      // Error handled by http client interceptor
    } finally {
      setLoading(false);
    }
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog
        showCloseButton={false}
        onBackdrop={() => onOpenChange(false)}
        onClose={() => onOpenChange(false)}
        className="!max-w-[560px] !border-0 !rounded-[14px] !bg-transparent !shadow-none"
      >
        <div className="ui-root">
          <form onSubmit={handleSubmit} className="ui-modal">
            <header className="ui-modal-header">
              <span className="ui-caption">New project</span>
              <div className="flex-1" />
              <button type="button" className="ui-btn-icon" aria-label="Close" onClick={() => onOpenChange(false)}>
                <span className="i-ph:x w-4 h-4" />
              </button>
            </header>

            <div className="ui-modal-body">
              <h2 className="ui-serif" style={{ fontSize: 24, margin: '0 0 20px' }}>
                Let&apos;s start fresh.
              </h2>

              <div style={{ marginBottom: 18 }}>
                <label htmlFor="project-name" className="ui-label">
                  Project name
                </label>
                <input
                  id="project-name"
                  type="text"
                  required
                  autoFocus
                  className="ui-input"
                  placeholder="ceramica-studio"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <span className="ui-label">Building</span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: 'var(--color-raised)',
                    border: '1px solid var(--color-hairline)',
                    borderRadius: 8,
                    padding: '12px 14px',
                  }}
                >
                  <span className="i-ph:browser" style={{ color: 'var(--color-accent)', fontSize: 18 }} />
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--color-primary)' }}>Landing page</div>
                    <div style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 2 }}>
                      Marketing site · React + Vite + Tailwind
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="project-description" className="ui-label">
                  Starting prompt
                </label>
                <textarea
                  id="project-description"
                  className="ui-input"
                  rows={3}
                  style={{ resize: 'vertical' }}
                  placeholder="A small ceramics studio site with a hero, lookbook, and contact form."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <footer className="ui-modal-footer flex-wrap">
              <span className="hidden sm:inline" style={{ fontSize: 11, color: 'var(--color-muted)' }}>
                Estimated build · ~1 min
              </span>
              <div className="flex-1" />
              <button type="button" className="ui-btn-ghost" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="ui-btn-accent" disabled={loading || !name.trim()}>
                {loading ? (
                  <>
                    <span className="i-ph:spinner-gap-bold animate-spin w-3.5 h-3.5" />
                    Creating
                  </>
                ) : (
                  'Create & build'
                )}
              </button>
            </footer>
          </form>
        </div>
      </Dialog>
    </RadixDialog.Root>
  );
}
